import React from 'react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { Box, Stack, Typography } from '@mui/material';
import { SUPPORT_EMAIL_MAILTO } from '../../constants/contactConstants';
import { AnalyticsEvents } from '../../enums/AnalyticsEvents';
import { isLocalHost } from '../../helpers/environmentHelper';
import {
  getPricingAudienceTranslationKey,
  priceToPerMoShort,
} from '../../helpers/pricingCardCopy';
import {
  getPricingV2CardBlock,
  isEnterpriseCustomer,
} from '../../helpers/pricingUpgradeRules';
import { useAnalytics } from '../../hooks/useAnalytics';
import Company from '../../interfaces/Company';
import { Price } from '../../interfaces/Price';
import type { TranslationKey } from '../../types/i18n';
import Button from '../shared/Button/Button';
import StyledPricingCard from './StyledPricingCard';
import StyledPricingCardHeader from './StyledPricingCardHeader';
import StyledRecommendedChip from './StyledRecommendedChip';
import { useTranslation } from '../../hooks/useTranslation';

/** Same query param as Manage billing (Stripe checkout / Payment Links). */
function appendPrefilledEmail(url: string, email: string): string {
  if (!url || url.startsWith('mailto:')) return url;
  try {
    const u = new URL(url);
    u.searchParams.set('prefilled_email', email);
    return u.toString();
  } catch {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}prefilled_email=${encodeURIComponent(email)}`;
  }
}

export interface PricingCardPlanSwitchContext {
  enabled: boolean;
  currentPlanId?: string | null;
  switchingPlanId: string | null;
  onSwitchPlan: (plan: Price) => void | Promise<void>;
}

interface PricingCardProps {
  plan: Price;
  currentUser: any;
  /** When set, applies pricing V2 upgrade rules (chat → calls = Enterprise only, etc.) */
  upgradeContext?: {
    activeV2Tab: 'call' | 'chat' | 'bundle';
    company: Company | null;
  };
  /** In-app Stripe subscription price change (paid subscribers); skips Payment Link checkout. */
  planSwitchContext?: PricingCardPlanSwitchContext;
  /** Optional UI displayed above the plan header (used for grouped tier dropdowns). */
  tierSelector?: React.ReactNode;
  /**
   * Lowest tier “/mo” price when this card groups multiple Stripe tiers (e.g. Billing).
   * If omitted, derived from `plan.price` when it looks like a monthly price.
   */
  startingAtMonthly?: string | null;
}

const PricingCard = ({
  plan,
  currentUser,
  upgradeContext,
  planSwitchContext,
  tierSelector,
  startingAtMonthly,
}: PricingCardProps) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();

  const onPlanLinkClick = (plan: Price) => {
    let url = isLocalHost() ? plan.test_url : plan.url;
    if (currentUser?.email && typeof url === 'string' && url.length > 0) {
      url = appendPrefilledEmail(url, currentUser.email);
    }
    window.location.href = url;
    track(AnalyticsEvents.SUBSCRIPTION_PURCHASE_VIEWED);
  };

  const navigateToPlanCheckout = () => {
    if (plan.id === 'consultation') {
      track(AnalyticsEvents.HELP_PAGE_CONSULTATION_BUTTON_CLICKED);
    }
    onPlanLinkClick(plan);
  };

  const handleChoosePlanDirect = () => {
    track(AnalyticsEvents.SUBSCRIPTION_PURCHASE_STARTED);
    navigateToPlanCheckout();
  };

  const isRecommended = plan.tier === 'business';
  const noStripe = plan.noStripe === true;
  const isEnterpriseNoStripe = noStripe && plan.tier === 'enterprise';
  const isFreeNoStripe = noStripe && plan.tier === 'free';

  const v2Block = upgradeContext
    ? getPricingV2CardBlock(
        upgradeContext.company,
        upgradeContext.activeV2Tab,
        plan
      )
    : { blocked: false as const };

  const audienceKey = getPricingAudienceTranslationKey(plan);
  const startingAtDisplay =
    startingAtMonthly != null && startingAtMonthly !== ''
      ? startingAtMonthly
      : priceToPerMoShort(plan.price);

  const isCurrentPlan =
    (planSwitchContext?.enabled === true &&
      plan.id === planSwitchContext.currentPlanId) ||
    (isEnterpriseNoStripe &&
      !!upgradeContext?.company &&
      isEnterpriseCustomer(upgradeContext.company));

  const renderCta = () => {
    if (v2Block.blocked) {
      const message =
        v2Block.reason === 'chat_add_calls_requires_enterprise'
          ? t('Enterprise is required to add calls to your chat subscription.')
          : t(
              'Downgrades are not available here. Use your billing portal or contact support.'
            );
      return (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {message}
          </Typography>
          <Button fullWidth disabled variant="outlined">
            {t('Not available')}
          </Button>
        </>
      );
    }
    if (isFreeNoStripe) {
      return (
        <Button fullWidth disabled>
          {t('Free plan')}
        </Button>
      );
    }
    if (isEnterpriseNoStripe) {
      if (
        upgradeContext?.company &&
        isEnterpriseCustomer(upgradeContext.company)
      ) {
        return (
          <Button fullWidth disabled variant="outlined">
            {t('Current plan')}
          </Button>
        );
      }
      if (plan.url || plan.test_url) {
        let href = isLocalHost() ? plan.test_url : plan.url;
        if (currentUser?.email && href && !String(href).startsWith('mailto:')) {
          href = appendPrefilledEmail(String(href), currentUser.email);
        }
        return (
          <a
            href={href || SUPPORT_EMAIL_MAILTO}
            style={{ width: '100%', textDecoration: 'none' }}
          >
            <Button fullWidth>{t('Contact Us')}</Button>
          </a>
        );
      }
      return (
        <Button fullWidth disabled>
          {t('Contact Us')}
        </Button>
      );
    }
    if (noStripe) {
      return (
        <Button fullWidth disabled>
          {t('Contact Us')}
        </Button>
      );
    }
    if (plan.price) {
      if (planSwitchContext?.enabled && !plan.noStripe) {
        const isCurrent = plan.id === planSwitchContext.currentPlanId;
        const busy = planSwitchContext.switchingPlanId === plan.id;
        if (isCurrent) {
          return (
            <Button fullWidth disabled variant="outlined">
              {t('Current plan')}
            </Button>
          );
        }
        return (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={!!planSwitchContext.switchingPlanId}
            onClick={() => {
              void Promise.resolve(planSwitchContext.onSwitchPlan(plan));
            }}
          >
            {busy ? t('Updating plan') : t('Switch plan')}
          </Button>
        );
      }
      return (
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleChoosePlanDirect}
        >
          {t('Choose')}
        </Button>
      );
    }
    return (
      <a href={plan.url} style={{ width: '100%', textDecoration: 'none' }}>
        <Button fullWidth>{t('Contact Us')}</Button>
      </a>
    );
  };

  return (
    <StyledPricingCard
      recommended={isRecommended}
      data-plan-id={plan.id}
      data-pricing-current-plan={isCurrentPlan ? 'true' : undefined}
    >
      {isRecommended && (
        <StyledRecommendedChip
          label={t('Recommended')}
          color="primary"
          variant="outlined"
          size="small"
        />
      )}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            pr: isRecommended ? 10 : 0,
            pt: isRecommended ? 0.5 : 0,
          }}
        >
          <StyledPricingCardHeader>
            <Typography
              variant="h5"
              component="h3"
              sx={theme => ({
                fontWeight: 600,
                letterSpacing: '-0.02em',
                lineHeight: 1.3,
                backgroundImage: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.info.main}, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                backgroundSize: '300% 100%',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                WebkitTextFillColor: 'transparent',
                '@keyframes planTitleGradient': {
                  '0%': { backgroundPosition: '0% 50%' },
                  '100%': { backgroundPosition: '200% 50%' },
                },
                animation: 'planTitleGradient 7s ease-in-out infinite',
              })}
            >
              {plan.title}
            </Typography>
            {audienceKey != null && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, mb: 0, lineHeight: 1.5 }}
              >
                {t(audienceKey as TranslationKey)}
              </Typography>
            )}
            {plan.price != null && plan.price !== '' && (
              <Typography
                variant="h4"
                component="p"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '-0.03em',
                  mt: 1.5,
                  mb: 0,
                  lineHeight: 1.2,
                }}
              >
                {plan.price}
              </Typography>
            )}
            {startingAtDisplay != null && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, mb: 0 }}
              >
                {t('Starting at {{price}}', { price: startingAtDisplay })}
              </Typography>
            )}
            {tierSelector != null && (
              <Box sx={{ mt: 1.5, width: '100%', maxWidth: '100%' }}>
                {tierSelector}
              </Box>
            )}
          </StyledPricingCardHeader>
        </Box>

        <Stack spacing={1.25} sx={{ flex: 1, mt: 2.5 }}>
          {plan.features?.map((feature: string, index: number) => (
            <Stack
              direction="row"
              spacing={1.25}
              key={index}
              alignItems="flex-start"
            >
              <CheckRoundedIcon
                sx={{
                  fontSize: 20,
                  color: 'success.main',
                  mt: 0.125,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.5 }}
              >
                {feature}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>

      <Box sx={{ mt: 'auto', pt: 3, width: '100%', flexShrink: 0 }}>
        {renderCta()}
      </Box>
    </StyledPricingCard>
  );
};

export default PricingCard;
